/**
 * Renders the estimate — or the specialist screen when no number may be shown.
 */

import {
  GOAL_INCOME_LATER,
  GOAL_ACCUMULATION,
  FEED_WARN,
  SPECIALIST_REASON_AGE,
  SPECIALIST_REASON_STALE,
  SPECIALIST_REASON_START_AGE,
} from './estimate.js';

const PERCENT_SCALE = 100;

const HEADING_CLASS =
  'tw-text-[1.5rem] m:tw-text-[1.75rem] tw-font-extrabold tw-leading-tight tw-text-brand-dark tw-mt-0';
const BODY_CLASS = 'tw-text-[1.0625rem] tw-leading-relaxed tw-text-dark-grey tw-mt-0';
const FINE_PRINT_CLASS = 'tw-text-[0.90625rem] tw-leading-relaxed [color:var(--qe-muted)]';

/** @param {number} amount @returns {string} */
const money = (amount) => `$${Math.round(amount).toLocaleString('en-US')}`;

/** @param {number} rate @returns {string} Decimal rate as a two-place percentage. */
const percent = (rate) => (rate * PERCENT_SCALE).toFixed(2);

/**
 * Substitutes {token} placeholders. An unknown token survives to the page, where a missing
 * config string is visible; blanking one would hide it.
 *
 * @param {string} template
 * @param {Object<string, string|number>} values
 * @returns {string}
 */
export function fill(template, values) {
  return Object.entries(values).reduce(
    (text, [token, value]) => text.replaceAll(`{${token}}`, String(value)),
    template
  );
}

const escapeHtml = (text) =>
  String(text).replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

/**
 * The "these rates are N days old" notice, rendered inside the figure block so a crop or a
 * screenshot cannot separate it from the number it qualifies.
 *
 * @param {Object} strings
 * @param {Object} estimate Result of estimator.estimate().
 * @returns {string}
 */
function staleNotice(strings, estimate) {
  if (estimate.feedStatus !== FEED_WARN) {
    return '';
  }

  return `<p class="tw-mt-3 tw-mb-0 tw-p-3 tw-rounded-lg tw-bg-secondary-tint tw-text-[0.90625rem] tw-text-dark-grey">${escapeHtml(
    fill(strings.results.stale_notice, { feed_age_days: estimate.feedAgeDays })
  )}</p>`;
}

/** @returns {string} */
function figureBlock(value, unit, strings, estimate) {
  return `
    <div class="c-quote-estimator__figure tw-my-5 tw-p-5 tw-rounded-xl tw-bg-primary-tint tw-text-center">
      <span class="tw-block tw-text-[2rem] m:tw-text-[2.5rem] tw-font-extrabold tw-leading-tight tw-text-primary">${escapeHtml(value)}</span>
      <span class="tw-block tw-mt-1 tw-text-base tw-text-dark-grey">${escapeHtml(unit)}</span>
      ${staleNotice(strings, estimate)}
    </div>`;
}

/** @returns {string} */
function termRows(rows, strings) {
  return `
    <div class="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
      ${rows
        .map(
          (row) => `
        <div class="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2 tw-p-4 tw-rounded-lg tw-border ${
          row.emphasis
            ? 'tw-border-secondary tw-bg-secondary-tint'
            : 'tw-border-light-grey tw-bg-white'
        }">
          <span class="tw-text-[1.0625rem] tw-font-bold tw-text-brand-dark">${row.term}-year term</span>
          <span class="tw-text-[1.125rem] tw-font-extrabold tw-text-primary">${escapeHtml(
            `${money(row.low)} – ${money(row.high)}`
          )}</span>
          <span class="tw-basis-full tw-text-sm [color:var(--qe-muted)]">${escapeHtml(
            fill(strings.results.term_row_sub, {
              rate_low: percent(row.rate.low),
              rate_high: percent(row.rate.high),
            })
          )}</span>
        </div>`
        )
        .join('')}
    </div>`;
}

/** @returns {string} */
function drivesList(strings, drivers) {
  return `
    <div class="tw-mt-5">
      <h3 class="tw-mt-0 tw-mb-2 tw-text-[1.0625rem] tw-font-extrabold tw-text-brand-dark">${escapeHtml(
        strings.results.drives_heading
      )}</h3>
      <ul class="tw-m-0 tw-pl-5 tw-flex tw-flex-col tw-gap-1.5 tw-text-base tw-leading-relaxed tw-text-dark-grey">
        ${drivers.map((driver) => `<li>${driver}</li>`).join('')}
      </ul>
    </div>`;
}

/** @returns {string} */
function disclaimer(strings) {
  return `<p class="tw-mt-4 tw-mb-0 ${FINE_PRINT_CLASS}">${escapeHtml(strings.results.disclaimer)}</p>`;
}

/** @returns {string} */
function specialistScreen(strings, estimate, asOfDisplay, guardrails) {
  const suffix =
    {
      [SPECIALIST_REASON_AGE]: 'age',
      [SPECIALIST_REASON_STALE]: 'stale',
      [SPECIALIST_REASON_START_AGE]: 'start_age',
    }[estimate.specialistReason] || 'amount';
  const values = {
    min_age_income: guardrails.min_age_income,
    start_age_max: guardrails.start_age_max,
    as_of_date: asOfDisplay,
  };

  return `
    <h2 class="js-qe-announce ${HEADING_CLASS} tw-mb-3">${escapeHtml(strings.specialist.headline)}</h2>
    <p class="${BODY_CLASS} tw-mb-4">${escapeHtml(
      fill(strings.specialist[`lead_${suffix}`], values)
    )}</p>
    <div class="tw-mb-4 tw-p-4 tw-rounded-lg tw-bg-primary-tint tw-text-base tw-leading-relaxed tw-text-dark-grey">${
      strings.specialist.notice
    }</div>
    <p class="tw-mt-0 tw-mb-0 ${FINE_PRINT_CLASS}">${escapeHtml(
      fill(strings.specialist[`note_${suffix}`], values)
    )}</p>`;
}

/**
 * Builds the results markup for one completed answer set.
 *
 * @param {Object} params
 * @param {Object} params.estimate Result of estimator.estimate().
 * @param {Object} params.estimator
 * @param {Object} params.answers Raw funnel answers.
 * @param {string} params.asOfDisplay Human-readable as-of date.
 * @returns {string} HTML.
 */
export function renderResults({ estimate, estimator, answers, asOfDisplay }) {
  const { config } = estimator;
  const { strings, guardrails } = config;

  if (!estimate.showsNumbers) {
    return specialistScreen(strings, estimate, asOfDisplay, guardrails);
  }

  const premium = money(estimate.premiumBasis);

  let basisText;
  let bodyHtml;
  const notes = [];
  let drivers;

  if (estimate.goal === GOAL_ACCUMULATION) {
    const rows = estimate.estimate.terms;
    const longestTerm = Math.max(...rows.map((row) => row.term));

    basisText = fill(strings.results.basis_myga, { premium });
    bodyHtml = `${termRows(rows, strings)}${staleNotice(strings, estimate)}`;
    drivers = [
      fill(strings.results.drives_term, { longest_term: longestTerm }),
      fill(strings.results.drives_amount, { premium }),
    ];
  } else if (estimate.goal === GOAL_INCOME_LATER) {
    const { deferralYears, deferralCapped, rateClamped } = estimate.estimate;
    const band = `${money(estimate.estimate.low)} – ${money(estimate.estimate.high)}`;

    basisText = fill(strings.results.basis_dia, {
      premium,
      age: answers.age,
      start_age: answers.startAge,
    });
    bodyHtml = figureBlock(
      band,
      fill(strings.results.unit_monthly_deferred, { start_age: answers.startAge }),
      strings,
      estimate
    );
    if (deferralCapped) {
      notes.push(
        fill(strings.results.deferral_capped, {
          max_deferral_years: config.income.max_deferral_years,
        })
      );
    }
    if (rateClamped) {
      notes.push(fill(strings.results.clamped_start_age, { max_rate_age: guardrails.max_rate_age }));
    }
    drivers = [
      fill(strings.results.drives_wait, {
        years: deferralYears,
        years_plural: deferralYears === 1 ? '' : 's',
      }),
      fill(strings.results.drives_start_age, { start_age: answers.startAge }),
    ];
  } else {
    const band = `${money(estimate.estimate.low)} – ${money(estimate.estimate.high)}`;

    basisText = fill(strings.results.basis_spia, { premium, age: answers.age });
    bodyHtml = figureBlock(band, strings.results.unit_monthly, strings, estimate);
    if (estimate.estimate.rateClamped) {
      notes.push(fill(strings.results.clamped_age, { max_rate_age: guardrails.max_rate_age }));
    }
    drivers = [
      fill(strings.results.drives_age, { age: answers.age }),
      fill(strings.results.drives_amount, { premium }),
    ];
  }

  return `
    <h2 class="js-qe-announce ${HEADING_CLASS} tw-mb-2">${escapeHtml(
      fill(strings.results.headline, { annuity_type: estimate.goal })
    )}</h2>
    <p class="${BODY_CLASS} tw-mb-0">${escapeHtml(basisText)}</p>
    ${bodyHtml}
    ${notes
      .map(
        (note) => `<p class="tw-mt-0 tw-mb-2 tw-text-base tw-text-dark-grey">${escapeHtml(note)}</p>`
      )
      .join('')}
    ${disclaimer(strings)}
    ${drivesList(strings, drivers)}`;
}
